import { useCallback, useEffect, useState } from "react";
import { ShieldAlert, ShieldCheck, Trash2, RotateCcw, Ban, KeyRound, Flag, RefreshCw, Download, Search } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { MfaSetupCard } from "./MfaSetupCard";

const STATUS_VIEWS = [
  { id: "pending", label: "Pending verification", status: "pending_email_verification" },
  { id: "quarantined", label: "Quarantined", status: "quarantined" },
  { id: "suspended", label: "Suspended", status: "suspended" },
];

const fmt = (v) => (v ? new Date(v).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export function SecurityAbuse() {
  const [overview, setOverview] = useState(null);
  const [alertItems, setAlertItems] = useState([]);
  const [alertTotal, setAlertTotal] = useState(0);
  const [alertPage, setAlertPage] = useState(1);
  const [view, setView] = useState("pending");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({});
  const [settings, setSettings] = useState(null);
  const [domainsText, setDomainsText] = useState("");
  const [auditItems, setAuditItems] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [cleanup, setCleanup] = useState(null);
  const [cleanupSel, setCleanupSel] = useState({});
  const [cleanupResult, setCleanupResult] = useState(null);
  const [cleanupJob, setCleanupJob] = useState(null);  // live background job progress

  // restore an in-flight cleanup job after a page refresh — progress is never lost,
  // and we jump straight to the Spam cleanup tab so the live panel is visible
  useEffect(() => {
    const jid = localStorage.getItem("ht_cleanup_job");
    if (jid) api.get(`/admin/security/cleanup-jobs/${jid}`)
      .then((r) => {
        setCleanupJob(r.data);
        setView("cleanup");  // always land on the Spam cleanup tab so the panel/report is visible
      })
      .catch(() => localStorage.removeItem("ht_cleanup_job"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // poll a running job every 2s until it finishes; back off and stop after repeated
  // errors (e.g. expired token) instead of hammering the API forever
  useEffect(() => {
    if (!cleanupJob || !["queued", "processing"].includes(cleanupJob.status)) return undefined;
    let errors = 0;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/admin/security/cleanup-jobs/${cleanupJob.id}`);
        errors = 0;
        setCleanupJob(r.data);
        if (!["queued", "processing"].includes(r.data.status)) {
          localStorage.removeItem("ht_cleanup_job");
          scanCleanup(); loadOverview();
        }
      } catch {
        errors += 1;
        if (errors >= 5) { clearInterval(t); fail({ response: { data: { detail: "Lost connection while watching the cleanup job — it keeps running in the background. Reload to re-attach." } } }, "Progress updates paused"); }
      }
    }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanupJob?.id, cleanupJob?.status]);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const note = (m) => { setMsg(m); setErr(""); };
  const fail = (e, fallback) => { setErr(formatApiError(e, fallback)); setMsg(""); };

  const loadOverview = useCallback(async () => {
    try { setOverview((await api.get("/admin/security/overview")).data); } catch { /* card stays empty */ }
  }, []);

  const loadAccounts = useCallback(async (v = view, p = page, query = q) => {
    const status = STATUS_VIEWS.find((s) => s.id === v)?.status;
    if (!status) return;
    try {
      const r = await api.get("/admin/security/accounts", { params: { status, q: query, page: p, limit: 25 } });
      setRows(r.data.items); setTotal(r.data.total); setSelected({});
    } catch (e) { fail(e, "Couldn't load accounts"); }
  }, [view, page, q]);

  const loadAudit = useCallback(async (p = auditPage) => {
    try {
      const r = await api.get("/admin/security/audit", { params: { page: p, limit: 50 } });
      setAuditItems(r.data.items); setAuditTotal(r.data.total);
    } catch (e) { fail(e, "Couldn't load the audit log"); }
  }, [auditPage]);

  const loadSettings = useCallback(async () => {
    try {
      const r = await api.get("/admin/security/settings");
      setSettings(r.data); setDomainsText((r.data.disposable_domains || []).join("\n"));
    } catch (e) { fail(e, "Couldn't load security settings"); }
  }, []);

  const loadAlerts = useCallback(async (p = alertPage) => {
    try {
      const r = await api.get("/admin/security/alerts", { params: { page: p, limit: 50 } });
      setAlertItems(r.data.items); setAlertTotal(r.data.total);
    } catch (e) { fail(e, "Couldn't load abuse alerts"); }
  }, [alertPage]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => {
    if (["pending", "quarantined", "suspended"].includes(view)) loadAccounts(view, page, q);
    if (view === "audit") loadAudit(auditPage);
    if (view === "alerts") loadAlerts(alertPage);
    if (view === "settings") loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, page, auditPage, alertPage]);

  const act = async (action, ids, { confirmDelete = false } = {}) => {
    if (!ids.length) return;
    const labels = { quarantine: "Quarantine", suspend: "Suspend", mark_spam: "Mark as spam", revoke_sessions: "Revoke all sessions for", restore: "Restore", delete: "PERMANENTLY DELETE" };
    const reason = window.prompt(`Reason for “${labels[action]}” (recorded in the audit log):`, action === "mark_spam" ? "Spam account" : "");
    if (reason === null) return;
    if (action === "delete" && !window.confirm(`Permanently delete ${ids.length} account(s)? This cannot be undone. Audit evidence is kept.`)) return;
    setBusy(action);
    try {
      const r = await api.post("/admin/security/action", { user_ids: ids, action, reason, confirm_delete: confirmDelete });
      note(`${labels[action]}: ${r.data.affected} account(s) updated.`);
      setSelected({}); setCleanupSel({});
      await loadOverview();
      if (["pending", "quarantined", "suspended"].includes(view)) await loadAccounts();
      if (view === "cleanup" && cleanup) await scanCleanup();
    } catch (e) { fail(e, "The security action failed"); }
    finally { setBusy(""); }
  };

  const scanCleanup = async () => {
    setBusy("scan"); setCleanup(null);
    try {
      const r = await api.get("/admin/security/cleanup-preview", { params: { days: 14 } });
      setCleanup(r.data); setCleanupSel({});
    } catch (e) { fail(e, "The cleanup scan failed"); }
    finally { setBusy(""); }
  };

  const runCleanup = async (action) => {
    const ids = Object.keys(cleanupSel).filter((k) => cleanupSel[k]);
    if (!ids.length) return;
    const text = action === "delete"
      ? `PERMANENTLY DELETE ${ids.length.toLocaleString("en-GB")} confirmed spam account(s)? Only quarantined/suspended accounts are deleted, and every account is re-checked right before deletion. The job runs in the background in safe batches with live progress — you can cancel or resume it, and refresh this page without losing it.`
      : `Quarantine ${ids.length.toLocaleString("en-GB")} account(s)? Sessions are revoked and they disappear publicly. Runs in the background in safe batches with live progress.`;
    if (!window.confirm(text)) return;
    setBusy(action); setCleanupResult(null);
    try {
      // async job: the request returns a job ID immediately — no long-running request,
      // no proxy timeout, no matter how many accounts are selected
      const r = await api.post("/admin/security/cleanup-jobs", { user_ids: ids, action, confirm: true });
      localStorage.setItem("ht_cleanup_job", r.data.job_id);
      const jr = await api.get(`/admin/security/cleanup-jobs/${r.data.job_id}`);
      setCleanupJob(jr.data);
      note(`Cleanup job started: ${r.data.total.toLocaleString("en-GB")} account(s) queued in safe background batches. Live progress below.`);
      setCleanupSel({});
    } catch (e) { fail(e, "The cleanup action failed"); }
    finally { setBusy(""); }
  };

  const cleanupJobAction = async (verb) => {
    try {
      if (verb === "dismiss") { localStorage.removeItem("ht_cleanup_job"); setCleanupJob(null); return; }
      await api.post(`/admin/security/cleanup-jobs/${cleanupJob.id}/${verb}`);
      const r = await api.get(`/admin/security/cleanup-jobs/${cleanupJob.id}`);
      setCleanupJob(r.data);
      if (verb === "resume") localStorage.setItem("ht_cleanup_job", cleanupJob.id);
    } catch (e) { fail(e, `Could not ${verb} the cleanup job`); }
  };

  const toggleRegistration = async () => {
    // kill switch applies IMMEDIATELY on click — no separate save step
    const next = !settings.registration_open;
    setBusy("settings");
    try {
      await api.put("/admin/security/settings", {
        registration_open: next,
        disposable_domains: domainsText.split("\n").map((d) => d.trim()).filter(Boolean),
      });
      setSettings({ ...settings, registration_open: next });
      note(next ? "Registration is now OPEN." : "Registration is now CLOSED.");
    } catch (e) { fail(e, "Couldn't save settings"); }
    finally { setBusy(""); }
  };

  const saveSettings = async () => {
    setBusy("settings");
    try {
      await api.put("/admin/security/settings", {
        registration_open: !!settings.registration_open,
        disposable_domains: domainsText.split("\n").map((d) => d.trim()).filter(Boolean),
      });
      note("Security settings saved.");
      await loadSettings();
    } catch (e) { fail(e, "Couldn't save settings"); }
    finally { setBusy(""); }
  };

  const downloadAudit = async () => {
    try {
      const r = await api.get("/admin/security/audit", { params: { format: "csv", limit: 1000 }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "security-audit.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { fail(e, "Couldn't export the audit log"); }
  };

  const selIds = Object.keys(selected).filter((k) => selected[k]);
  const toggleAll = (list, sel, setSel) => {
    const all = list.every((r) => sel[r.id]);
    setSel(all ? {} : Object.fromEntries(list.map((r) => [r.id, true])));
  };

  const accountTable = (list, sel, setSel) => (
    <div className="overflow-x-auto border border-zinc-200 rounded-md" data-testid="security-accounts-table">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-left text-xs text-zinc-500">
          <tr>
            <th className="p-3 w-8"><input type="checkbox" data-testid="security-select-all"
              checked={list.length > 0 && list.every((r) => sel[r.id])}
              onChange={() => toggleAll(list, sel, setSel)} /></th>
            <th className="p-3">Account</th><th className="p-3">Status</th><th className="p-3">Risk</th>
            <th className="p-3">Flags / reason</th><th className="p-3">Signup</th><th className="p-3">Registered</th><th className="p-3">Last active</th>
          </tr>
        </thead>
        <tbody>
          {list.map((u) => (
            <tr key={u.id} className="border-t border-zinc-100 align-top" data-testid={`security-row-${u.id}`}>
              <td className="p-3"><input type="checkbox" checked={!!sel[u.id]} data-testid={`security-select-${u.id}`}
                onChange={(e) => setSel({ ...sel, [u.id]: e.target.checked })} /></td>
              <td className="p-3">
                <p className="font-medium text-zinc-900 break-all max-w-[220px]">{u.name || "—"}</p>
                <p className="text-xs text-zinc-500 break-all">{u.email}</p>
                <p className="text-xs text-zinc-400">{u.role} · {u.email_domain}</p>
              </td>
              <td className="p-3">
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                  u.account_status === "active" ? "bg-green-100 text-green-800"
                  : u.account_status === "pending_email_verification" ? "bg-amber-100 text-amber-800"
                  : u.account_status === "quarantined" ? "bg-orange-100 text-orange-800"
                  : "bg-red-100 text-red-800"}`} data-testid={`security-status-${u.id}`}>
                  {u.account_status.replaceAll("_", " ")}
                </span>
                {!u.email_verified && <p className="text-[11px] text-zinc-400 mt-1">email unverified</p>}
              </td>
              <td className="p-3 font-mono text-xs" data-testid={`security-risk-${u.id}`}>{u.risk_score || 0}</td>
              <td className="p-3 text-xs text-zinc-600 max-w-[260px]">
                {(u.risk_flags || []).map((f) => <p key={f} className="mb-0.5">• {f}</p>)}
                {u.quarantine_reason && <p className="text-orange-700">{u.quarantine_reason}</p>}
                {u.suspend_reason && <p className="text-red-700">{u.suspend_reason}</p>}
              </td>
              <td className="p-3 text-xs text-zinc-500">
                <p>{u.registered_via || "direct"}</p>
                {u.reg_subnet && <p title={`IP hash ${u.reg_ip_hash}`}>net {u.reg_subnet}</p>}
                {u.reg_user_agent && <p className="truncate max-w-[140px]" title={u.reg_user_agent}>{u.reg_user_agent}</p>}
              </td>
              <td className="p-3 text-xs text-zinc-500 whitespace-nowrap">{fmt(u.created_at)}</td>
              <td className="p-3 text-xs text-zinc-500 whitespace-nowrap">{fmt(u.last_seen)}</td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-zinc-400">Nothing here.</td></tr>}
        </tbody>
      </table>
    </div>
  );

  return (
    <section className="space-y-5" data-testid="security-abuse-section">
      <div className="flex items-center gap-2">
        <ShieldAlert className="h-5 w-5 text-zinc-700" />
        <h2 className="text-lg font-bold text-zinc-950">Security & abuse</h2>
      </div>

      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="security-overview">
          {[
            { label: "Pending verification", value: overview.pending_email_verification, tone: "text-amber-700" },
            { label: "Quarantined", value: overview.quarantined, tone: "text-orange-700" },
            { label: "Suspended", value: overview.suspended, tone: "text-red-700" },
            { label: "Active accounts", value: overview.active, tone: "text-green-700" },
            { label: "Signups (24h)", value: overview.registrations_24h, tone: "text-zinc-900" },
          ].map((c) => (
            <div key={c.label} className="border border-zinc-200 rounded-md p-3">
              <p className={`text-xl font-bold ${c.tone}`}>{c.value}</p>
              <p className="text-xs text-zinc-500">{c.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="security-subnav">
        {[...STATUS_VIEWS.map((v) => ({ id: v.id, label: v.label })),
          { id: "cleanup", label: "Spam cleanup" }, { id: "alerts", label: "Abuse alerts" },
          { id: "audit", label: "Audit log" }, { id: "settings", label: "Settings" }].map((v) => (
          <button key={v.id} onClick={() => { setView(v.id); setPage(1); }} data-testid={`security-view-${v.id}`}
            className={`h-9 px-3 rounded-md text-sm font-medium transition-colors duration-150 ${view === v.id ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}>
            {v.label}
          </button>
        ))}
      </div>

      {msg && <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-4 py-2" data-testid="security-msg">{msg}</p>}
      {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-2" data-testid="security-error">{err}</p>}

      {["pending", "quarantined", "suspended"].includes(view) && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name…"
                data-testid="security-search"
                className="h-10 pl-9 pr-3 border border-zinc-300 rounded-md text-sm w-64" />
            </div>
            <button onClick={() => loadAccounts(view, page, q)} data-testid="security-search-btn"
              className="h-10 px-3 bg-zinc-950 text-white text-sm rounded-md">Search</button>
            {selIds.length > 0 && (
              <div className="flex flex-wrap gap-2 ml-auto" data-testid="security-bulk-bar">
                <button disabled={!!busy} onClick={() => act("quarantine", selIds)} data-testid="security-bulk-quarantine"
                  className="h-9 px-3 text-sm rounded-md bg-orange-600 text-white disabled:opacity-50"><Ban className="inline h-3.5 w-3.5 mr-1" />Quarantine ({selIds.length})</button>
                <button disabled={!!busy} onClick={() => act("mark_spam", selIds)} data-testid="security-bulk-spam"
                  className="h-9 px-3 text-sm rounded-md bg-red-600 text-white disabled:opacity-50"><Flag className="inline h-3.5 w-3.5 mr-1" />Mark as spam</button>
                <button disabled={!!busy} onClick={() => act("revoke_sessions", selIds)} data-testid="security-bulk-revoke"
                  className="h-9 px-3 text-sm rounded-md bg-zinc-700 text-white disabled:opacity-50"><KeyRound className="inline h-3.5 w-3.5 mr-1" />Revoke sessions</button>
                <button disabled={!!busy} onClick={() => act("restore", selIds)} data-testid="security-bulk-restore"
                  className="h-9 px-3 text-sm rounded-md bg-green-700 text-white disabled:opacity-50"><RotateCcw className="inline h-3.5 w-3.5 mr-1" />Restore</button>
                <button disabled={!!busy} onClick={() => act("delete", selIds, { confirmDelete: true })} data-testid="security-bulk-delete"
                  className="h-9 px-3 text-sm rounded-md bg-red-800 text-white disabled:opacity-50"><Trash2 className="inline h-3.5 w-3.5 mr-1" />Delete</button>
              </div>
            )}
          </div>
          {accountTable(rows, selected, setSelected)}
          <div className="flex items-center gap-3 text-sm text-zinc-500">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="security-prev"
              className="h-8 px-3 border border-zinc-300 rounded-md disabled:opacity-40">Previous</button>
            <span>Page {page} · {total} account(s)</span>
            <button disabled={page * 25 >= total} onClick={() => setPage(page + 1)} data-testid="security-next"
              className="h-8 px-3 border border-zinc-300 rounded-md disabled:opacity-40">Next</button>
          </div>
        </>
      )}

      {view === "cleanup" && (
        <div className="space-y-4" data-testid="security-cleanup">
          <p className="text-sm text-zinc-600 max-w-2xl">
            Review-first scan for the September 2026 spam wave: repeated casino text, shortened links, identical
            profile content, unverified accounts with no activity. Nothing changes until you confirm. Admins and
            anyone with genuine activity (applications, jobs, messages) are never listed.
          </p>
          <div className="flex gap-2">
            <button onClick={scanCleanup} disabled={!!busy} data-testid="security-cleanup-scan"
              className="h-10 px-4 bg-zinc-950 text-white text-sm font-semibold rounded-md disabled:opacity-50">
              <RefreshCw className={`inline h-4 w-4 mr-1 ${busy === "scan" ? "animate-spin" : ""}`} />
              {busy === "scan" ? "Scanning…" : "Scan for spam accounts"}
            </button>
            {cleanup && (
              <>
                <button onClick={() => runCleanup("quarantine")} disabled={!!busy || !Object.values(cleanupSel).some(Boolean)}
                  data-testid="security-cleanup-quarantine"
                  className="h-10 px-4 bg-orange-600 text-white text-sm font-semibold rounded-md disabled:opacity-50">Quarantine selected</button>
                <button onClick={() => runCleanup("delete")} disabled={!!busy || !Object.values(cleanupSel).some(Boolean)}
                  data-testid="security-cleanup-delete"
                  className="h-10 px-4 bg-red-800 text-white text-sm font-semibold rounded-md disabled:opacity-50">Delete selected permanently</button>
              </>
            )}
          </div>
          {cleanupJob && (
            <div className="border border-zinc-200 rounded-md bg-white p-4 space-y-3" data-testid="cleanup-job-progress">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-zinc-900" data-testid="cleanup-job-title">
                  Cleanup job · {cleanupJob.action === "delete" ? "permanent deletion" : "quarantine"} ·{" "}
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                    cleanupJob.status === "completed" ? "bg-green-100 text-green-800"
                    : cleanupJob.status === "processing" ? "bg-blue-100 text-blue-800"
                    : cleanupJob.status === "queued" ? "bg-zinc-100 text-zinc-700"
                    : cleanupJob.status === "cancelled" ? "bg-amber-100 text-amber-800"
                    : "bg-red-100 text-red-800"}`} data-testid="cleanup-job-status">
                    {cleanupJob.status === "processing" ? `processing — batch ${cleanupJob.batches_done + 1}` : cleanupJob.status}
                  </span>
                </p>
                <div className="flex gap-2">
                  {cleanupJob.can_cancel && (
                    <button onClick={() => cleanupJobAction("cancel")} data-testid="cleanup-job-cancel"
                      className="h-8 px-3 border border-zinc-300 rounded-md text-xs font-semibold text-zinc-700 hover:border-zinc-500">Cancel safely</button>
                  )}
                  {cleanupJob.can_resume && (
                    <button onClick={() => cleanupJobAction("resume")} data-testid="cleanup-job-resume"
                      className="h-8 px-3 bg-blue-700 text-white rounded-md text-xs font-semibold hover:bg-blue-800">
                      Resume ({cleanupJob.remaining.toLocaleString("en-GB")} left)</button>
                  )}
                  {!cleanupJob.can_cancel && !cleanupJob.can_resume && (
                    <button onClick={() => cleanupJobAction("dismiss")} data-testid="cleanup-job-dismiss"
                      className="h-8 px-3 border border-zinc-300 rounded-md text-xs font-semibold text-zinc-700 hover:border-zinc-500">Dismiss</button>
                  )}
                </div>
              </div>
              <div className="h-2 rounded-full bg-zinc-100 overflow-hidden" data-testid="cleanup-job-bar">
                <div className="h-full bg-blue-700 transition-all duration-500" style={{ width: `${cleanupJob.percent}%` }} />
              </div>
              <p className="text-xs text-zinc-500" data-testid="cleanup-job-counts">
                {cleanupJob.processed.toLocaleString("en-GB")} / {cleanupJob.total.toLocaleString("en-GB")} processed ({cleanupJob.percent}%)
                {" · batches of "}{cleanupJob.batch_size} · every account re-checked before action
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center" data-testid="cleanup-job-report">
                {(cleanupJob.action === "delete"
                  ? [["Permanently deleted", cleanupJob.deleted, "text-red-700"]]
                  : [["Quarantined", cleanupJob.quarantined, "text-orange-700"]]
                ).concat([
                  ["Skipped — protected", cleanupJob.skipped_protected, "text-zinc-700"],
                  ["Skipped — activity found", cleanupJob.skipped_activity, "text-green-700"],
                  ["Already removed", cleanupJob.already_deleted, "text-zinc-500"],
                  ["Failed (retryable)", cleanupJob.failed, cleanupJob.failed ? "text-red-700" : "text-zinc-500"],
                ]).map(([label, value, tone]) => (
                  <div key={label} className="border border-zinc-100 rounded-md p-2">
                    <p className={`text-lg font-bold ${tone}`} data-testid={`cleanup-report-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>{value.toLocaleString("en-GB")}</p>
                    <p className="text-[10px] text-zinc-400 leading-tight">{label}</p>
                  </div>
                ))}
              </div>
              {cleanupJob.last_error && (
                <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="cleanup-job-error">
                  {cleanupJob.last_error} — the job can be resumed; processed accounts are never repeated.
                </p>
              )}
              {cleanupJob.skip_samples?.length > 0 && (
                <details className="text-xs" data-testid="cleanup-job-samples">
                  <summary className="cursor-pointer text-zinc-500 hover:text-zinc-700">
                    Skipped accounts — exact reasons ({cleanupJob.skipped_protected + cleanupJob.skipped_activity + cleanupJob.already_deleted + cleanupJob.failed})
                  </summary>
                  <ul className="mt-1.5 space-y-0.5 text-zinc-600 max-h-40 overflow-y-auto">
                    {cleanupJob.skip_samples.map((s, i) => (
                      <li key={`${s.id}-${i}`} data-testid={`cleanup-skip-${i}`}>{s.email || s.id} — {s.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
          {cleanupResult && (
            <div className="border border-amber-200 bg-amber-50 rounded-md p-3" data-testid="security-cleanup-skipped">
              <p className="text-sm font-semibold text-amber-900">{cleanupResult.skipped.length} account(s) were skipped:</p>
              <ul className="mt-1 space-y-0.5 text-xs text-amber-800 max-h-40 overflow-y-auto">
                {cleanupResult.skipped.map((s, i) => (
                  <li key={s.id || i} data-testid={`cleanup-skipped-${i}`}>{s.email || s.id} — {s.reason}</li>
                ))}
              </ul>
            </div>
          )}
          {cleanup && (
            <>
              <p className="text-sm text-zinc-600" data-testid="security-cleanup-count">
                {cleanup.count} account(s) match the spam pattern (scanned {cleanup.scanned} recent signups). Review before acting.
                {" "}<strong data-testid="security-cleanup-selected">{Object.values(cleanupSel).filter(Boolean).length.toLocaleString("en-GB")} selected</strong>
                {" — select all covers every page of the review list, not just the visible rows."}
              </p>
              {accountTable(cleanup.candidates.map((c) => ({ ...c, risk_flags: c.match_reasons })), cleanupSel, setCleanupSel)}
            </>
          )}
        </div>
      )}

      {view === "alerts" && (
        <div className="space-y-3" data-testid="security-alerts">
          <p className="text-sm text-zinc-600 max-w-2xl">
            Automatic abuse monitoring runs every 5 minutes — registration spikes, suspicious subnets,
            email surges, failed-login spikes and rate-limit block surges. The office is emailed once per
            signal per hour; every signal is recorded here.
          </p>
          <div className="overflow-x-auto border border-zinc-200 rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-left text-xs text-zinc-500">
                <tr><th className="p-3">When</th><th className="p-3">Signal</th><th className="p-3">Detail</th><th className="p-3">Office emailed</th></tr>
              </thead>
              <tbody>
                {alertItems.map((a) => (
                  <tr key={a.id} className="border-t border-zinc-100" data-testid={`security-alert-${a.id}`}>
                    <td className="p-3 text-xs text-zinc-500 whitespace-nowrap">{fmt(a.at)}</td>
                    <td className="p-3 text-xs font-mono">{a.kind}</td>
                    <td className="p-3 text-xs text-zinc-600">{a.detail}</td>
                    <td className="p-3 text-xs">{a.notified ? "Yes" : "—"}</td>
                  </tr>
                ))}
                {alertItems.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-zinc-400">No abuse signals recorded.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3 text-sm text-zinc-500">
            <button disabled={alertPage <= 1} onClick={() => setAlertPage(alertPage - 1)} data-testid="security-alerts-prev"
              className="h-8 px-3 border border-zinc-300 rounded-md disabled:opacity-40">Previous</button>
            <span>Page {alertPage} · {alertTotal} signal(s)</span>
            <button disabled={alertPage * 50 >= alertTotal} onClick={() => setAlertPage(alertPage + 1)} data-testid="security-alerts-next"
              className="h-8 px-3 border border-zinc-300 rounded-md disabled:opacity-40">Next</button>
          </div>
        </div>
      )}

      {view === "audit" && (
        <div className="space-y-3" data-testid="security-audit">
          <div className="flex justify-end">
            <button onClick={downloadAudit} data-testid="security-audit-csv"
              className="h-9 px-3 text-sm border border-zinc-300 rounded-md"><Download className="inline h-3.5 w-3.5 mr-1" />Export CSV</button>
          </div>
          <div className="overflow-x-auto border border-zinc-200 rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-left text-xs text-zinc-500">
                <tr><th className="p-3">When</th><th className="p-3">Actor</th><th className="p-3">Action</th><th className="p-3">Account</th><th className="p-3">Reason</th></tr>
              </thead>
              <tbody>
                {auditItems.map((a) => (
                  <tr key={a.id} className="border-t border-zinc-100" data-testid={`security-audit-${a.id}`}>
                    <td className="p-3 text-xs text-zinc-500 whitespace-nowrap">{fmt(a.at)}</td>
                    <td className="p-3 text-xs">{a.actor}</td>
                    <td className="p-3 text-xs font-mono">{a.action}</td>
                    <td className="p-3 text-xs break-all">{a.target_email}</td>
                    <td className="p-3 text-xs text-zinc-600 max-w-[280px]">{a.reason}</td>
                  </tr>
                ))}
                {auditItems.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-zinc-400">No security events yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3 text-sm text-zinc-500">
            <button disabled={auditPage <= 1} onClick={() => setAuditPage(auditPage - 1)} data-testid="security-audit-prev"
              className="h-8 px-3 border border-zinc-300 rounded-md disabled:opacity-40">Previous</button>
            <span>Page {auditPage} · {auditTotal} event(s)</span>
            <button disabled={auditPage * 50 >= auditTotal} onClick={() => setAuditPage(auditPage + 1)} data-testid="security-audit-next"
              className="h-8 px-3 border border-zinc-300 rounded-md disabled:opacity-40">Next</button>
          </div>
        </div>
      )}

      {view === "settings" && <MfaSetupCard />}
      {view === "settings" && !settings && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-4 py-3 max-w-xl" data-testid="security-settings-locked">
          Security settings couldn't be loaded — refresh the page to try again.
        </p>
      )}
      {view === "settings" && settings && (
        <div className="space-y-5 max-w-xl" data-testid="security-settings">
          <div className="border border-red-200 bg-red-50 rounded-md p-4">
            <p className="text-sm font-semibold text-red-900 flex items-center gap-2">
              {settings.registration_open ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
              Public registration is currently {settings.registration_open ? "OPEN" : "CLOSED"}
            </p>
            <p className="text-xs text-red-800 mt-1">
              While closed, nobody can create an account (email/password or Google) — existing users can still log in.
              Even when opened, registration also requires the Cloudflare Turnstile secret to be configured, or it stays blocked.
            </p>
            <button onClick={toggleRegistration} disabled={!!busy}
              data-testid="security-toggle-registration"
              className={`mt-3 h-10 px-4 text-sm font-semibold rounded-md text-white disabled:opacity-50 ${settings.registration_open ? "bg-red-700" : "bg-green-700"}`}>
              {settings.registration_open ? "Close registration" : "Open registration"}
            </button>
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-700 block mb-1.5">Blocked disposable email domains (one per line)</label>
            <textarea value={domainsText} onChange={(e) => setDomainsText(e.target.value)} rows={5}
              data-testid="security-domains"
              className="w-full border border-zinc-300 rounded-md p-3 text-sm font-mono" placeholder="mailinator.com" />
            <p className="text-xs text-zinc-400 mt-1">A built-in list always applies; these are extras. Gmail/Outlook etc. are never blocked.</p>
          </div>
          <button onClick={saveSettings} disabled={!!busy} data-testid="security-save-settings"
            className="h-10 px-4 bg-zinc-950 text-white text-sm font-semibold rounded-md disabled:opacity-50">
            {busy === "settings" ? "Saving…" : "Save settings"}
          </button>
        </div>
      )}
    </section>
  );
}
